import { GetStaticProps } from 'next';
import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

const AIPoweredPedagogyStudy = () => {
  return (
    <LandingPageLayout
      title="Research Study: AI-Powered Pedagogy for Nursing Students | Curipod"
      subTitle="A randomized controlled trial found that Curipod significantly enhanced nursing students' reflective thinking, emotional competence, and clinical embeddedness with moderate to large effect sizes."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>RESEARCH STUDY</HeroTag>
            <HeroTitle>
              AI-Powered Pedagogy to Enhance Reflective Thinking, Emotional Competence,
              and Clinical Embeddedness in University Students
            </HeroTitle>
            <HeroMeta>
              <MetaBadge>University / Nursing</MetaBadge>
              <MetaBadge>Randomized Controlled Trial</MetaBadge>
              <MetaBadge>2025 Study</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>142</StatNumber>
                  <StatLabel>Nursing Students</StatLabel>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>RCT</StatNumber>
                  <StatLabel>Randomized Controlled Trial</StatLabel>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <EffectSizeNumber>d = 0.3&ndash;0.8</EffectSizeNumber>
                  <StatLabel>Moderate to Large Effect Sizes</StatLabel>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* ABSTRACT */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Abstract</SectionTitle>
            <SectionText>
              <strong>Background:</strong> Given the centrality of AI in the learning and
              clinical paradigms of nursing students, it is essential to investigate its
              impact on their mental mastery.
            </SectionText>
            <SectionText>
              <strong>Aim:</strong> To determine the effect of AI-driven smart learning
              platforms on reflective thinking, emotional competence, and clinical
              embeddedness among nursing students.
            </SectionText>
            <SectionText>
              <strong>Methods:</strong> A randomized controlled trial design was employed
              with 142 nursing students who completed a survey assessing the measured
              constructs. The intervention group participated in a structured, intensive
              training program delivered entirely online via the Curipod smart platform,
              while the control group received conventional lectures supplemented with
              PowerPoint presentations and textbooks.
            </SectionText>
            <SectionText>
              <strong>Results:</strong> The intervention group showed significant
              improvements in reflective thinking, emotional competence, and clinical
              embeddedness compared to the control group at T2, with gains maintained at
              T3. Effect sizes ranged from moderate to large, demonstrating the robustness
              and consistency of the intervention&apos;s impact (d = 0.301&ndash;0.800).
            </SectionText>
            <SectionText>
              <strong>Conclusion:</strong> AI-driven smart learning platforms
              significantly enhanced nursing students&apos; cognitive, emotional, and
              clinical competencies. These findings support the integration of AI-enhanced
              learning strategies to advance nursing education and professional
              preparedness.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* INTRODUCTION */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Introduction</SectionTitle>
            <SectionText>
              The adoption of artificial intelligence (AI) represents a transformative
              shift in the rapidly evolving field of education, introducing innovative
              teaching and learning approaches. Leveraging these advancements, Curipod, an
              AI-driven smart learning platform, provides interactive lessons, adaptive
              feedback, and simulation-based activities. Its distinctive
              features&mdash;including real-time polls, word-cloud brainstorming, and
              personalized learning pathways&mdash;position it as a versatile tool for
              fostering both cognitive and emotional competencies in clinical education.
            </SectionText>
            <SectionText>
              AI has shown considerable potential in offering timely support to university
              students, improving both their educational outcomes and overall experiences.
              According to a recent report by the Digital Education Council (2024), 86% of
              students regularly use AI in their studies, with 54% engaging with these
              tools on a weekly basis.
            </SectionText>
            <SectionText>
              In psychiatric nursing curricula, the advent of AI-driven smart learning
              platforms, powered by technological advancements and machine learning
              algorithms, fosters deeper cognitive engagement and reflective thinking.
              Reflective thinking is a critical component of higher-order cognition,
              enabling learners to analyze, evaluate, and synthesize information to
              develop deeper insights and problem-solving abilities.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* DESIGN & METHODS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Design</SectionTitle>
            <SectionText>
              A randomized controlled trial design was adopted to compare the
              effectiveness of the Curipod platform application with traditional
              educational methods. Data collection was conducted from March 2025 to May
              2025 at the Faculty of Nursing, Beni-Suef University, Egypt.
            </SectionText>
            <SectionText>
              A simple random allocation method was employed to assign eligible
              participants to either the intervention or control group. Students were
              explicitly informed that their decision to participate or decline would not
              affect their academic standing.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Results</SectionTitle>
            <SectionText>
              Around three quarters of the studied group were below 23 years (78.9%) and
              from rural areas (73.9%). When analyzing the baseline stage before
              intervention (T1), the scores were similar between groups, indicating no
              initial significant differences.
            </SectionText>
            <SectionText>
              The intervention group showed significant improvements in reflective
              thinking, emotional competence, and clinical embeddedness compared to the
              control group at T2, with gains maintained at T3. Effect sizes ranged from
              moderate to large, demonstrating the robustness and consistency of the
              intervention&apos;s impact (d = 0.301&ndash;0.800).
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* DISCUSSION */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Discussion</SectionTitle>
            <SectionText>
              The adoption of artificial intelligence (AI) tools in nursing education is
              transforming the academic journey of students by enhancing their learning
              capabilities, supporting professional development, and facilitating
              continuing education. AI-driven applications play a critical role in
              preparing nursing students for future clinical responsibilities by promoting
              the mastery of core competencies essential for safe and effective practice.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* CONCLUSION */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Conclusion</SectionTitle>
            <SectionText>
              The intervention significantly enhanced nursing students&apos; core outcomes
              in reflective thinking, emotional competence, and clinical embeddedness.
              Future research should include a wider range of academic disciplines and
              adopt a randomized controlled trial (RCT) nested within a mixed-methods
              design to comprehensively capture students&apos; experiences across diverse
              nursing courses. Insights from such research could guide educational
              policymakers in adopting AI-enhanced learning strategies that support
              professional preparedness.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* SOURCE & CTA */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <CenteredSection>
            <SourceCard>
              <SourceLabel>SOURCE</SourceLabel>
              <SourceText>
                Eweida, R. S., Altheeb, M., Elsehrawy, M. G., Ali, H. A. A. E. H.,
                Soliman, M. W., &amp; Sorour, D. M. (2025). AI-Powered pedagogy to enhance
                reflective thinking, emotional competence, and clinical embeddedness in
                university students: An RCT study.{' '}
                <em>Teaching and Learning in Nursing</em>. In Press.
              </SourceText>
              <a
                href="https://www.sciencedirect.com/science/article/abs/pii/S1557308725003725"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SourceLink>View on ScienceDirect &rarr;</SourceLink>
              </a>
            </SourceCard>

            <GetStartedWrapper>
              <Link href="/signup" passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default AIPoweredPedagogyStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: ${theme.colors.paleBeige};
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  color: ${theme.colors.black};
  margin: 0 0 20px;
  line-height: 1.15;
  max-width: 800px;

  @media ${MOBILE} {
    font-size: 36px;
  }

  @media (max-width: 320px) {
    font-size: 28px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const StatBannerWrapper = styled.div`
  background: ${theme.colors.black};
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 20px;

  @media ${MOBILE} {
    gap: 20px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 48px;

  @media ${MOBILE} {
    width: 100%;
    height: 1px;
    margin: 0;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 16px 24px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${({ $highlight }) => ($highlight ? theme.colors.orange : theme.colors.white)};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const EffectSizeNumber = styled.div`
  font-size: 36px;
  font-weight: bold;
  color: ${theme.colors.orange};
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 26px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  a {
    color: ${theme.colors.orange};
    text-decoration: underline;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── SOURCE & CTA ──────────────────────────────────────────────────────────── */

const SourceCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 24px 32px;
  box-shadow: ${theme.effects.defaultShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 20px;
  }
`;

const SourceLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  margin-bottom: 8px;
`;

const SourceText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0 0 8px;
`;

const SourceLink = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orange};
  &:hover {
    text-decoration: underline;
  }
`;

const GetStartedWrapper = styled.div`
  margin-top: 8px;
`;
